import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getFellow, updateProfile, changePassword } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { UserCircleIcon, KeyIcon } from '@heroicons/react/24/outline';

function Field({ label, field, type = 'text', form, setForm }) {
  return (
    <div>
      <label className="text-xs text-gray-500 uppercase tracking-wider font-medium block mb-1">{label}</label>
      <input
        type={type}
        className="input"
        value={form[field]}
        onChange={(e) => setForm(f => ({ ...f, [field]: e.target.value }))}
      />
    </div>
  );
}

export default function ProfileSettingsPage() {
  const { user, updateUser } = useAuth();
  const qc = useQueryClient();
  const [profileForm, setProfileForm] = useState({
    full_name: '', email: '',
    university: '', degree: '', project_title: '', supervisor: '',
    phone: '', research_interests: '', linkedin_url: '', bio: '', current_status: 'Active',
    thru_project: '', project_type: '', location: '', start_date: '', expected_completion: '',
  });
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });

  const { data } = useQuery({
    queryKey: ['fellow', user?.id],
    queryFn: () => getFellow(user?.id),
    enabled: !!user?.id,
  });

  useEffect(() => {
    if (data?.data) {
      const p = data.data;
      setProfileForm({
        full_name: p.user?.full_name || '',
        email: p.user?.email || '',
        university: p.university || '',
        degree: p.degree || '',
        project_title: p.project_title || '',
        supervisor: p.supervisor || '',
        phone: p.phone || '',
        research_interests: p.research_interests || '',
        linkedin_url: p.linkedin_url || '',
        bio: p.bio || '',
        current_status: p.current_status || 'Active',
        thru_project: p.thru_project || '',
        project_type: p.project_type || '',
        location: p.location || '',
        start_date: p.start_date ? p.start_date.split('T')[0] : '',
        expected_completion: p.expected_completion ? p.expected_completion.split('T')[0] : '',
      });
    }
  }, [data]);

  const profileMut = useMutation({
    mutationFn: (payload) => updateProfile(user?.id, payload),
    onSuccess: () => {
      qc.invalidateQueries(['fellow', user?.id]);
      if (profileForm.full_name) updateUser({ full_name: profileForm.full_name });
      if (profileForm.email) updateUser({ email: profileForm.email });
      toast.success('Profile saved!');
    },
    onError: () => toast.error('Failed to save profile'),
  });

  const pwMut = useMutation({
    mutationFn: changePassword,
    onSuccess: () => { toast.success('Password changed!'); setPwForm({ current_password: '', new_password: '', confirm: '' }); },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to change password'),
  });

  const handlePwSubmit = (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm) { toast.error('Passwords do not match'); return; }
    pwMut.mutate({ current_password: pwForm.current_password, new_password: pwForm.new_password });
  };

  return (
    <div className="max-w-2xl space-y-5">
      <div>
        <h1 className="text-xl font-bold text-white">Profile Settings</h1>
        <p className="text-gray-500 text-sm">Manage your fellowship profile and account</p>
      </div>

      {/* Account info */}
      <div className="card flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-primary-600/30 flex items-center justify-center text-primary-400 text-2xl font-bold">
          {user?.full_name?.[0]?.toUpperCase()}
        </div>
        <div>
          <div className="font-semibold text-white">{user?.full_name}</div>
          <div className="text-gray-500 text-sm">{user?.email}</div>
          <span className={`badge mt-1 ${user?.role === 'admin' ? 'bg-red-900/50 text-red-300' : 'bg-blue-900/50 text-blue-300'}`}>
            {user?.role}
          </span>
        </div>
      </div>

      {/* Profile form */}
      <div className="card">
        <h2 className="font-semibold text-white flex items-center gap-2 mb-4">
          <UserCircleIcon className="w-4 h-4 text-primary-400" /> Fellowship Profile
        </h2>
        <form onSubmit={(e) => { e.preventDefault(); profileMut.mutate(profileForm); }} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Full Name" field="full_name" form={profileForm} setForm={setProfileForm} />
            <Field label="Email Address" field="email" type="email" form={profileForm} setForm={setProfileForm} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="University" field="university" form={profileForm} setForm={setProfileForm} />
            <Field label="Degree / Programme" field="degree" form={profileForm} setForm={setProfileForm} />
            <Field label="Supervisor" field="supervisor" form={profileForm} setForm={setProfileForm} />
            <Field label="Phone" field="phone" form={profileForm} setForm={setProfileForm} />
            <Field label="LinkedIn URL" field="linkedin_url" form={profileForm} setForm={setProfileForm} />
            <Field label="Location" field="location" form={profileForm} setForm={setProfileForm} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wider font-medium block mb-1">THRU ZIM Project</label>
              <select className="input" value={profileForm.thru_project} onChange={(e) => setProfileForm(f => ({ ...f, thru_project: e.target.value }))}>
                <option value="">— Select project —</option>
                {['Y-Check','Neotree','KOSHESAI','PROMISE','Fractures E3','Helen Jack'].map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <Field label="Project Type (quant / qual / mixed)" field="project_type" form={profileForm} setForm={setProfileForm} />
            <Field label="MSc Start Date" field="start_date" type="date" form={profileForm} setForm={setProfileForm} />
            <Field label="Expected Completion" field="expected_completion" type="date" form={profileForm} setForm={setProfileForm} />
          </div>
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-wider font-medium block mb-1">Project Title</label>
            <input className="input" value={profileForm.project_title} onChange={(e) => setProfileForm(f => ({ ...f, project_title: e.target.value }))} />
          </div>
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-wider font-medium block mb-1">Research Interests</label>
            <input className="input" value={profileForm.research_interests} onChange={(e) => setProfileForm(f => ({ ...f, research_interests: e.target.value }))} />
          </div>
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-wider font-medium block mb-1">Bio</label>
            <textarea className="input resize-none" rows={3} value={profileForm.bio} onChange={(e) => setProfileForm(f => ({ ...f, bio: e.target.value }))} />
          </div>
          <button type="submit" className="btn-primary" disabled={profileMut.isPending}>
            {profileMut.isPending ? 'Saving...' : 'Save Profile'}
          </button>
        </form>
      </div>

      {/* Password change */}
      <div className="card">
        <h2 className="font-semibold text-white flex items-center gap-2 mb-4">
          <KeyIcon className="w-4 h-4 text-primary-400" /> Change Password
        </h2>
        <form onSubmit={handlePwSubmit} className="space-y-3">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Current Password</label>
            <input type="password" className="input" value={pwForm.current_password} onChange={(e) => setPwForm(f => ({ ...f, current_password: e.target.value }))} required />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">New Password</label>
            <input type="password" className="input" value={pwForm.new_password} onChange={(e) => setPwForm(f => ({ ...f, new_password: e.target.value }))} required minLength={8} />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Confirm New Password</label>
            <input type="password" className="input" value={pwForm.confirm} onChange={(e) => setPwForm(f => ({ ...f, confirm: e.target.value }))} required />
          </div>
          <button type="submit" className="btn-primary" disabled={pwMut.isPending}>
            {pwMut.isPending ? 'Updating...' : 'Update Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
